// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the input layer (principle #23). One interface, three implementations, all
// producing the SAME thing the core consumes: a mote HEIGHT (0..1) per tick.
//   · mic       — voice pitch → height (mic.ts). The primary input on a phone.
//   · manual    — a finger slid up/down anywhere on the board (absolute, so the thumb
//                 never reaches across the play area), or ↑/↓ keys. The FALLBACK — a real
//                 continuous-control game in its own right, built first; never a wall.
//   · synthetic — a scripted height series for the browser test hook, to prove mic→game.
// The sensor is an INPUT, never a gate. Permission is asked from a tap (getUserMedia).

import { HeightSmoother, clamp } from './pitch';

export type SourceKind = 'mic' | 'manual' | 'synthetic';

export interface HeightSource {
  readonly kind: SourceKind;
  /** Advance by `dtMs` and return the current mote height (0..1). */
  height(dtMs: number): number;
  /** Recentre the mote — a fresh course. */
  reset(): void;
  dispose(): void;
}

/* ── the touch / keyboard fallback ──────────────────────────────────────────── */

const KEY_RATE = 0.0022; // height units per ms while an arrow/W/S is held

export interface ManualSource extends HeightSource {
  kind: 'manual';
}

/**
 * Slide a finger up/down anywhere on `el` to set the mote's height (absolute — top of
 * the board is high, bottom is low), or hold ↑/↓ (W/S) to glide it. The mote is at a
 * fixed x, so the finger never covers it and never reaches across the play area.
 */
export function createManualSource(el: HTMLElement): ManualSource {
  const smoother = new HeightSmoother(0.5, 60);
  let target = 0.5;
  let pointerDown = false;
  let keyDir = 0; // -1 down, +1 up, 0 none
  const held = new Set<string>();

  const heightFromEvent = (e: PointerEvent): number => {
    const r = el.getBoundingClientRect();
    if (r.height <= 0) return target;
    // a small inset margin so the very top/bottom are reachable without pixel-perfect aim
    const pad = r.height * 0.08;
    return clamp(1 - (e.clientY - r.top - pad) / (r.height - 2 * pad), 0, 1);
  };

  const onDown = (e: PointerEvent): void => {
    pointerDown = true;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
    target = heightFromEvent(e);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent): void => {
    if (!pointerDown) return;
    target = heightFromEvent(e);
  };
  const onUp = (): void => {
    pointerDown = false;
  };

  const KEYS_UP = new Set(['ArrowUp', 'w', 'W']);
  const KEYS_DOWN = new Set(['ArrowDown', 's', 'S']);
  const onKeyDown = (e: KeyboardEvent): void => {
    if (KEYS_UP.has(e.key)) {
      held.add('up');
      keyDir = 1;
      e.preventDefault();
    } else if (KEYS_DOWN.has(e.key)) {
      held.add('down');
      keyDir = -1;
      e.preventDefault();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (KEYS_UP.has(e.key)) held.delete('up');
    else if (KEYS_DOWN.has(e.key)) held.delete('down');
    keyDir = held.has('up') ? 1 : held.has('down') ? -1 : 0;
  };

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    kind: 'manual',
    height(dtMs: number): number {
      if (keyDir !== 0) target = clamp(target + keyDir * KEY_RATE * dtMs, 0, 1);
      return smoother.to(target, dtMs);
    },
    reset(): void {
      target = 0.5;
      smoother.set(0.5);
    },
    dispose(): void {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}

/* ── the browser test hook (synthetic height feed) ──────────────────────────── */

export interface SyntheticSource extends HeightSource {
  kind: 'synthetic';
  /** Set the height the next height() calls will report. */
  set(h: number): void;
}

export function createSyntheticSource(): SyntheticSource {
  let h = 0.5;
  return {
    kind: 'synthetic',
    height: () => h,
    set(v: number) {
      h = clamp(v, 0, 1);
    },
    reset() {
      h = 0.5;
    },
    dispose() {},
  };
}
